import React, { useState } from 'react';
import { Mail, MessageSquare, Phone, Send, MapPin } from 'lucide-react';
import { MarketingLayout } from '../../layouts/MarketingLayout';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';

const channels = [
  { icon: Mail, label: 'Email', value: 'support@keskedo.com', href: 'mailto:support@keskedo.com' },
  { icon: MessageSquare, label: 'Live Chat', value: 'Available 24/7 in app', href: '#' },
  { icon: Phone, label: 'Phone', value: '+44 (0) 20 4538 5678', href: 'tel:+442045385678' },
];

const offices = [
  { city: 'London', address: '14 Old Street, London EC1V 9BG, UK' },
  { city: 'New York', address: '450 Park Avenue, New York, NY 10022, USA' },
  { city: 'Lisbon', address: 'Av. da Liberdade 110, 1250-146 Lisboa, Portugal' },
];

export const Contact = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <MarketingLayout
      title="Contact us"
      subtitle="Pick the channel that suits you best. Our team typically responds within a few hours."
    >
      <section className="space-y-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {channels.map((c, i) => (
            <a
              key={i}
              href={c.href}
              className="p-6 bg-white border border-slate-200 rounded-2xl space-y-3 hover:shadow-lg hover:border-indigo-100 transition-all"
            >
              <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                <c.icon size={18} />
              </div>
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">{c.label}</p>
                <p className="text-sm font-semibold text-slate-900">{c.value}</p>
              </div>
            </a>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 space-y-6">
          <div className="space-y-1">
            <h2 className="text-xl font-bold text-slate-900">Send us a message</h2>
            <p className="text-sm text-slate-500">We typically respond within a few hours.</p>
          </div>

          {submitted ? (
            <div className="p-6 bg-emerald-50 text-emerald-800 rounded-xl text-sm">
              Thanks — your message is on its way. We'll be in touch shortly.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Name" type="text" required placeholder="Jane Doe" />
                <Input label="Email" type="email" required placeholder="name@example.com" />
              </div>
              <Input label="Subject" type="text" required placeholder="How can we help?" />
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Message</label>
                <textarea
                  required
                  rows={5}
                  placeholder="Tell us what's on your mind..."
                  className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                />
              </div>
              <Button type="submit" className="inline-flex items-center gap-2">
                Send Message <Send size={16} />
              </Button>
            </form>
          )}
        </div>

        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-900">Offices</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {offices.map((o, i) => (
              <div key={i} className="p-6 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                <div className="flex items-center gap-2 font-bold text-slate-900">
                  <MapPin size={14} /> {o.city}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{o.address}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};
